import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { User, UserRole } from '../../types';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';
import {
  Users as UsersIcon,
  UserPlus,
  Search,
  Filter,
  UserCheck,
  UserX,
  Shield,
  GraduationCap,
  Edit2,
  Trash2,
  LogIn,
  X,
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Phone,
  Mail,
  User as UserAvatar,
} from 'lucide-react';

export const UserManager: React.FC = () => {
  const {
    users,
    currentUser,
    createUser,
    updateUser,
    deleteUser,
    impersonateUser,
    fetchUsers,
  } = useApp();
  const { showToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'student' as UserRole,
    status: 'active' as 'active' | 'inactive',
  });
  const [formError, setFormError] = useState('');

  // Compute User Metrics
  const totalUsers = users.length;
  const studentCount = users.filter((u) => u.role === 'student').length;
  const adminCount = users.filter((u) => u.role === 'admin').length;
  const activeCount = users.filter((u) => u.status === 'active' || !u.status).length;
  const inactiveCount = users.filter((u) => u.status === 'inactive').length;

  // Filtered Users List
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.mobile && u.mobile.includes(searchTerm));
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' ? u.status === 'active' || !u.status : u.status === 'inactive');
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleOpenAddModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      mobile: '',
      password: '',
      role: 'student',
      status: 'active',
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleOpenEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      mobile: user.mobile || '',
      password: '',
      role: user.role,
      status: user.status || 'active',
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      if (editingUser) {
        const res = await updateUser(editingUser.id, {
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password || undefined,
          role: formData.role,
          status: formData.status,
        });
        if (res.success) {
          showToast('User profile updated successfully', 'success', 'User Updated');
          setModalOpen(false);
        } else {
          setFormError(res.error || 'Failed to update user.');
        }
      } else {
        if (!formData.password) {
          setFormError('Password is required for new users.');
          setIsSubmitting(false);
          return;
        }
        const res = await createUser({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
          role: formData.role,
          status: formData.status,
        });
        if (res.success) {
          showToast('New user account created successfully', 'success', 'User Created');
          setModalOpen(false);
        } else {
          setFormError(res.error || 'Failed to create user.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmUser) return;
    setIsSubmitting(true);

    try {
      const res = await deleteUser(deleteConfirmUser.id);
      if (res.success) {
        showToast(
          res.message || 'User and history permanently deleted',
          'success',
          'User Deleted'
        );
        setDeleteConfirmUser(null);
      } else {
        showToast(res.error || 'Failed to delete user.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [impersonateTarget, setImpersonateTarget] = useState<User | null>(null);

  const handleImpersonate = async (user: User) => {
    if (user.id === currentUser?.id) {
      showToast('You are already logged in as this user.', 'info');
      return;
    }
    if (user.status === 'inactive') {
      showToast('Cannot login as an inactive user account.', 'error');
      return;
    }

    setImpersonateTarget(user);
  };

  const confirmImpersonate = async () => {
    if (!impersonateTarget) return;
    const user = impersonateTarget;
    setImpersonateTarget(null);
    const res = await impersonateUser(user.id);
    if (res.success) {
      showToast(`Now logged in as ${user.name}`, 'success', 'Impersonation Active');
    } else {
      showToast(res.error || 'Impersonation failed', 'error');
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      {/* 1. Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-[#E5E9E9] shadow-[0_2px_12px_rgba(17,17,17,0.02)]">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#1b8e88] uppercase tracking-wider mb-1">
            <UsersIcon className="w-4 h-4 text-[#1fb4ac]" />
            User Management Directory
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-[#111111] tracking-tight">
            Accounts & Permissions
          </h1>
          <p className="text-xs text-[#5F6368] mt-1">
            Manage student registrations, administrator privileges, and perform secure session impersonation.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer shrink-0"
        >
          <UserPlus className="w-4 h-4 text-[#111111]" />
          Add New User
        </button>
      </div>

      {/* 2. Metrics Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">Total Users</div>
          <div className="text-xl sm:text-2xl font-black text-[#111111]">{totalUsers}</div>
          <div className="text-[10px] text-[#5F6368] mt-0.5">Database total</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">Students</div>
          <div className="text-xl sm:text-2xl font-black text-[#1b8e88]">{studentCount}</div>
          <div className="text-[10px] text-[#5F6368] mt-0.5">Learner accounts</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">Admins</div>
          <div className="text-xl sm:text-2xl font-black text-[#8a6508]">{adminCount}</div>
          <div className="text-[10px] text-[#5F6368] mt-0.5">System managers</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">Active Status</div>
          <div className="text-xl sm:text-2xl font-black text-[#20A66A]">{activeCount}</div>
          <div className="text-[10px] text-[#5F6368] mt-0.5">Enabled access</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs col-span-2 sm:col-span-1">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">Inactive Status</div>
          <div className="text-xl sm:text-2xl font-black text-[#D94A5C]">{inactiveCount}</div>
          <div className="text-[10px] text-[#5F6368] mt-0.5">Disabled access</div>
        </div>
      </div>

      {/* 3. Search & Filter Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-2xs flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-[#5F6368] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or mobile..."
            className="w-full pl-10 pr-4 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-medium focus:outline-none focus:border-[#56E8E1] transition-all"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto justify-end">
          <div className="flex items-center gap-1.5 bg-[#F7FAFA] px-3 py-1.5 rounded-xl border border-[#E5E9E9]">
            <Filter className="w-3.5 h-3.5 text-[#5F6368]" />
            <span className="text-xs font-bold text-[#5F6368]">Role:</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="bg-transparent text-xs font-bold text-[#111111] focus:outline-none cursor-pointer"
            >
              <option value="all">All Roles</option>
              <option value="student">Student</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 bg-[#F7FAFA] px-3 py-1.5 rounded-xl border border-[#E5E9E9]">
            <span className="text-xs font-bold text-[#5F6368]">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="bg-transparent text-xs font-bold text-[#111111] focus:outline-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* 4. Users Table */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] shadow-[0_2px_12px_rgba(17,17,17,0.02)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F7FAFA] border-b border-[#E5E9E9] text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
                <th className="py-3.5 px-4 sm:px-6">User Profile</th>
                <th className="py-3.5 px-4">Contact Info</th>
                <th className="py-3.5 px-4">Role</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Created Date</th>
                <th className="py-3.5 px-4 text-right sm:pr-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E9E9] text-xs">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-[#5F6368]">
                    <div className="max-w-xs mx-auto space-y-2">
                      <UserX className="w-8 h-8 mx-auto text-[#5F6368]/40" />
                      <div className="font-bold text-sm text-[#111111]">No matching users found</div>
                      <p className="text-xs">Try adjusting your search keywords or role/status filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const isCurrent = currentUser?.id === u.id;
                  const isActive = u.status === 'active' || !u.status;

                  return (
                    <tr key={u.id} className="hover:bg-[#F7FAFA]/80 transition-colors">
                      <td className="py-4 px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs ${
                            u.role === 'admin' 
                              ? 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/30' 
                              : 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/30'
                          }`}>
                            {u.name ? u.name.charAt(0).toUpperCase() : <UserAvatar className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="font-bold text-[#111111] flex items-center gap-1.5">
                              {u.name}
                              {isCurrent && (
                                <span className="px-1.5 py-0.2 rounded bg-[#E8FAF9] text-[#1b8e88] text-[10px] font-extrabold border border-[#56E8E1]/40">
                                  You
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-[#5F6368]">ID: #{u.id}</div>
                          </div>
                        </div>
                      </td>

                      <td className="py-4 px-4">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1 text-[#111111] font-medium">
                            <Mail className="w-3 h-3 text-[#5F6368]" />
                            {u.email}
                          </div>
                          {u.mobile && (
                            <div className="flex items-center gap-1 text-[11px] text-[#5F6368]">
                              <Phone className="w-3 h-3 text-[#5F6368]" />
                              {u.mobile}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold capitalize ${
                          u.role === 'admin'
                            ? 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40'
                            : 'bg-[#F0F4F4] text-[#111111] border border-[#E5E9E9]'
                        }`}>
                          {u.role === 'admin' ? (
                            <Shield className="w-3 h-3 text-[#8a6508]" />
                          ) : (
                            <GraduationCap className="w-3 h-3 text-[#1fb4ac]" />
                          )}
                          {u.role}
                        </span>
                      </td>

                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold capitalize ${
                          isActive
                            ? 'bg-[#EAF8F1] text-[#20A66A] border border-[#20A66A]/20'
                            : 'bg-[#FDEDEF] text-[#D94A5C] border border-[#D94A5C]/20'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#20A66A]' : 'bg-[#D94A5C]'}`} />
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="py-4 px-4 text-[#5F6368] font-medium text-[11px]">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                      </td>

                      <td className="py-4 px-4 text-right sm:pr-6">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleImpersonate(u)}
                            disabled={isCurrent || !isActive}
                            className={`p-1.5 rounded-lg border transition-all cursor-pointer flex items-center gap-1 text-[11px] font-bold ${
                              isCurrent || !isActive
                                ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400'
                                : 'bg-[#E8FAF9] hover:bg-[#56E8E1]/30 text-[#1b8e88] border-[#56E8E1]/40'
                            }`}
                            title={isCurrent ? 'Current Session' : !isActive ? 'Account Inactive' : 'Login as User'}
                          >
                            <LogIn className="w-3.5 h-3.5" />
                            <span className="hidden xl:inline">Login as User</span>
                          </button>

                          <button
                            onClick={() => handleOpenEditModal(u)}
                            className="p-1.5 text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] rounded-lg border border-transparent hover:border-[#E5E9E9] transition-all cursor-pointer"
                            title="Edit User"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => setDeleteConfirmUser(u)}
                            disabled={isCurrent}
                            className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                              isCurrent
                                ? 'opacity-30 cursor-not-allowed text-gray-400 border-transparent'
                                : 'text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] border-transparent hover:border-[#D94A5C]/20'
                            }`}
                            title={isCurrent ? 'Cannot delete logged in admin' : 'Delete or Deactivate User'}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Add / Edit User Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
          <div className="bg-white rounded-3xl border border-[#E5E9E9] shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E9] bg-[#F7FAFA]">
              <h3 className="font-extrabold text-base text-[#111111]">
                {editingUser ? `Edit User — ${editingUser.name}` : 'Add New User'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1 text-[#5F6368] hover:text-[#111111] rounded-lg hover:bg-white transition-all cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-[#FDEDEF] border border-[#D94A5C]/30 text-[#D94A5C] rounded-xl text-xs font-semibold flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{formError}</div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Full Name <span className="text-[#D94A5C]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. John Doe"
                  className="w-full px-3.5 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-medium focus:outline-none focus:border-[#56E8E1] transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Email Address <span className="text-[#D94A5C]">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full px-3.5 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-medium focus:outline-none focus:border-[#56E8E1] transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="e.g. 9876543210"
                    className="w-full px-3.5 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-medium focus:outline-none focus:border-[#56E8E1] transition-all"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-[#111111]">
                    Password {editingUser ? <span className="font-normal text-[#5F6368]">(Leave blank to keep existing)</span> : <span className="text-[#D94A5C]">*</span>}
                  </label>
                  <span className="text-[10px] text-[#5F6368]">Min 6 characters</span>
                </div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-[#5F6368] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    required={!editingUser}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'Enter new password to update' : 'Enter account password'}
                    className="w-full pl-9 pr-3.5 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-medium focus:outline-none focus:border-[#56E8E1] transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Account Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    className="w-full px-3 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-bold focus:outline-none focus:border-[#56E8E1] transition-all cursor-pointer"
                  >
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Account Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-3 py-2 bg-[#F7FAFA] border border-[#E5E9E9] rounded-xl text-xs font-bold focus:outline-none focus:border-[#56E8E1] transition-all cursor-pointer"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 bg-white hover:bg-[#F0F4F4] text-[#111111] font-bold text-xs rounded-xl border border-[#E5E9E9] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : editingUser ? 'Save Changes' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirmUser}
        title="Permanently Delete User?"
        message={
          deleteConfirmUser
            ? `Are you sure you want to permanently delete user account ${deleteConfirmUser.name} (${deleteConfirmUser.email})? All exam/revision history, answers and scores will be permanently deleted and cannot be undone.`
            : ''
        }
        confirmText="Delete Permanently"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmUser(null)}
      />

      <ConfirmModal
        isOpen={!!impersonateTarget}
        title="Impersonate User"
        message={
          impersonateTarget
            ? `Start session as ${impersonateTarget.name} (${impersonateTarget.email})?`
            : ''
        }
        confirmText="Start Session"
        variant="info"
        onConfirm={confirmImpersonate}
        onCancel={() => setImpersonateTarget(null)}
      />
    </div>
  );
};
